import React, { PureComponent, Fragment, useState, useEffect } from 'react';
import { InfoCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import '@ant-design/compatible/assets/index.css';
import {
  Input,
  Button,
  Checkbox,
  Row,
  Col,
  message,
  Modal,
  Tooltip,
  Form,
  Radio,
  Space,
  Divider,
  Switch,
  Steps,
  Popover,
  Select,
} from 'antd';
import { connect } from 'dva';
import Label from '@/components/Label/Label';
import styles from './AdvancedAWSConfiguration.module.css';
import CoverageStatusConnections from '../CoverageStatusConnections';
import MoodUpdateModal from './MoodUpdateModal';
import ModeCards from '@/components/ModeCards';
import { COLOR } from '@/utils/color';
import { Icon } from '@ant-design/compatible';
import AwsSvgIcon from '@/../public/aws-1.svg';
import TestConnectionContent from './Components/TestConnectionContent';
import DeployStackContent from './Components/DeployStackContent';
import { INSTRUCTION_CONTENT, tooltipContent } from '@/utils/constant';
import PopoverContent from './Components/PopoverContent';
import { showDeleteConfirm } from '../showDeleteConfirm';
import DeleteCloudAccountWrapper from './Components/DeleteCloudAccountWrapper';
import { decryptAES } from '@/utils/utils';

const cardData = [
  {
    title: 'ADVANCED (Recommended)',
    description: `Complete Cloudnosys security suite, including <br/><b>CloudEye:</b> Misconfigurations Detection
    <br/><b>Cloudxray:</b> Malware & OS Vulnerabilities
    <br/><b>EagleEye:</b> Real-time Threat Detection`,
    subDescription: `Setup time 10-15 mins`,
    key: 'advanced',
    status: '',
  },
  {
    title: 'BASIC (CloudEye)',
    description: `Catalog your cloud resources and detect misconfiguration level violations<br/>(Cloud security posture management, CSPM)`,
    subDescription: `Setup time > 5 mins`,
    key: 'basic',
    status: '',
  },
];

const { confirm } = Modal;

const AdvancedAWSConfiguration = props => {
  const { cloudAccountData, updateData, xrayEnabledCloudProviders } = props;
  const [downgradingMoodModal, setDowngradingMoodModal] = useState(false);
  const [modeCheckbox, setModeCheckbox] = useState(cloudAccountData.protectionLevel);

  const [form] = Form.useForm();

  // * Form on finish
  const onFinish = values => {
    updateData(values);
  };

  // * this function is used to validate the form before submit/update
  const onFinishFailed = ({ errorFields }) => {
    form.scrollToField(errorFields[0].name);
  };

  useEffect(() => {
    if (modeCheckbox !== cloudAccountData.protectionLevel) {
      confirmDowngradeModal();
    }
  }, [modeCheckbox]);

  const handleDowngradeAccount = async () => {
    const { dispatch } = props;

    await dispatch({
      type: 'cloudAccounts/updateCloudAccountMode',
      payload: {
        cloudAccountId: cloudAccountData.id,
        protectionLevel: 'basic',
      },
    });

    const payload = {
      ...cloudAccountData,
      protectionLevel: 'basic',
    };

    runScan(payload);
    setDowngradingMoodModal(true);
    Modal.destroyAll();
  };

  const runScan = accountData => {
    const { dispatch } = props;

    // Get currently scanning cloud accounts Ids
    let currentlyScanningCloudAccountsIds = getCurrentlyScanningCloudAccountsIds({
      shouldFilterWithStartScanStatus: true,
    });

    // Reset scan progress if currently no cloud account is scanning
    if (currentlyScanningCloudAccountsIds.length === 0) {
      dispatch({
        type: 'cloudAccount/clearScanProgress',
      });
    }

    dispatch({
      type: 'scan/runScan',
      payload: {
        cloudAccounts: [accountData],
        orgId: accountData.orgId,
        currentlyScanningCloudAccountsIds,
      },
    });

    dispatch({
      type: 'scopeReport/current',
    });
  };

  const getCurrentlyScanningCloudAccountsIds = ({ shouldFilterWithStartScanStatus = true }) => {
    const { allCloudAccountsList } = props;

    // Get currently scanning cloud accounts Ids
    const currentlyScanningCloudAccountsIds = allCloudAccountsList
      .filter(
        cloudAccount =>
          cloudAccount.status === 'running-scan' ||
          (shouldFilterWithStartScanStatus && cloudAccount.status === 'start-scan')
      )
      .map(cloudAccount => cloudAccount.id);

    return currentlyScanningCloudAccountsIds;
  };

  const confirmDowngradeModal = () => {
    confirm({
      title: (
        <p
          style={{
            margin: 0,
            fontSize: '1em',
          }}
        >
          Downgrade Cloud Account?
        </p>
      ),
      content: (
        <p
          style={{
            margin: 0,
            fontSize: '0.8em',
          }}
        >
          This will downgrade your scanning mode to Basic (misconfigurations detection). To avoid
          costs associated with the Advanced mode, please make sure to delete the stack named
          'Cloudnosys-Scanner', to delete the deployed resources from your AWS account.
        </p>
      ),
      okText: 'Downgrade',
      centered: true,
      async onOk() {
        await handleDowngradeAccount();
      },
      onCancel() {
        setModeCheckbox(cloudAccountData.protectionLevel);
      },
    });
  };

  return (
    <>
      <Form onFinish={onFinish} form={form}>
        <Row justify="space-between" gutter={24}>
          <Col span={12}>
            <Row style={{ marginBottom: 44 }}>
              <Col span={24}>
                <Label
                  style={{
                    marginBottom: 10,
                  }}
                  title="Name"
                />

                <Row gutter={18}>
                  <Col span={18}>
                    <Form.Item
                      required
                      name="accountName"
                      initialValue={cloudAccountData.accountName || ''}
                      rules={[
                        {
                          required: true,
                          message: 'Please enter your AWS Account Name',
                        },
                      ]}
                      style={{
                        marginBottom: 0,
                      }}
                    >
                      <Input placeholder="AWS Account Name" />
                    </Form.Item>
                  </Col>
                  <Col
                    span={2}
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Popover content={tooltipContent.cloudAccountName}>
                      <QuestionCircleOutlined
                        style={{
                          verticalAlign: 0,
                          fontSize: '20px',
                          color: 'rgba(0, 0, 0, 0.15)',
                        }}
                      />
                    </Popover>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row style={{ marginBottom: 44 }}>
              <Col span={24}>
                <Label
                  style={{
                    marginBottom: 10,
                  }}
                  title="External ID"
                />

                <Row gutter={18}>
                  <Col span={18}>
                    <Form.Item
                      required
                      name="AWSExternalID"
                      initialValue={cloudAccountData.externalId || ''}
                      rules={[
                        {
                          // required: this.state.checkNick,
                          message: 'Please enter your AWS External ID ',
                        },
                      ]}
                      style={{
                        marginBottom: 0,
                      }}
                    >
                      <Input placeholder="AWS External ID" />
                    </Form.Item>
                  </Col>
                  <Col
                    span={2}
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Popover
                      content={
                        <PopoverContent fileContent={INSTRUCTION_CONTENT.CHANGE_EXTERNAL_ID} />
                      }
                    >
                      <QuestionCircleOutlined
                        style={{
                          verticalAlign: 0,
                          fontSize: '20px',
                          color: 'rgba(0, 0, 0, 0.15)',
                        }}
                      />
                    </Popover>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row style={{ marginBottom: 44 }}>
              <Col span={24}>
                <Label
                  style={{
                    marginBottom: 10,
                  }}
                  title="Role ARN"
                />
                <Row gutter={18}>
                  <Col span={18}>
                    <Form.Item
                      // style={{ color: 'rgba(0,0,0,.45)' }}
                      // label={<RoleARNTooltip />}
                      required
                      name="RoleARN"
                      initialValue={decryptAES(cloudAccountData.roleArn).replaceAll('"', '') || ''}
                      rules={[
                        {
                          // required: this.state.checkNick,
                          message: 'Please enter your Role ARN',
                        },
                      ]}
                      style={{
                        marginBottom: 0,
                      }}
                    >
                      <Input disabled placeholder="Role ARN" />
                    </Form.Item>
                  </Col>
                  <Col
                    span={2}
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Popover content={tooltipContent.roleArn}>
                      <QuestionCircleOutlined
                        style={{
                          verticalAlign: 0,
                          fontSize: '20px',
                          color: 'rgba(0, 0, 0, 0.15)',
                        }}
                      />
                    </Popover>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row style={{ marginBottom: 44 }}>
              <Col span={24}>
                <Label
                  style={{
                    marginBottom: 10,
                  }}
                  title="Monitoring"
                />
                <Row gutter={18}>
                  <Col span={18}>
                    <Form.Item
                      required
                      name="interval"
                      initialValue={cloudAccountData.interval || 'Off'}
                      rules={[
                        {
                          message: 'Please select monitoring status',
                        },
                      ]}
                      style={{
                        marginBottom: 0,
                      }}
                    >
                      <Select>
                        <Option value="Off">Off</Option>
                        <Option value="daily">Daily</Option>
                        <Option value="weekly">Weekly</Option>
                        <Option value="monthly">Monthly</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col
                    span={2}
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Popover content={tooltipContent.monitoring}>
                      <QuestionCircleOutlined
                        style={{
                          verticalAlign: 0,
                          fontSize: '20px',
                          color: 'rgba(0, 0, 0, 0.15)',
                        }}
                      />
                    </Popover>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'start',
                marginBottom: 44,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                }}
              >
                <Col>
                  <Label title="Account Status" />
                </Col>
                <Col>
                  <Form.Item required name="isActive" initialValue={cloudAccountData.isActive}>
                    <Switch
                      size="small"
                      checkedChildren={<Icon type="check" />}
                      unCheckedChildren={<Icon type="close" />}
                      defaultChecked={cloudAccountData.isActive}
                    />
                  </Form.Item>
                </Col>
              </div>
              <Col>
                <p>
                  Currently your Account is &nbsp;
                  <b
                    style={{
                      color: cloudAccountData.isActive ? COLOR.ACTIVE : COLOR.DISABLED,
                    }}
                  >
                    {cloudAccountData.isActive ? 'Active' : 'Inactive'}
                  </b>
                </p>
              </Col>
            </Row>
            {xrayEnabledCloudProviders.includes(cloudAccountData.provider.toLowerCase()) && (
              <>
                <Row
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'start',
                    marginBottom: 44,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                    }}
                  >
                    <Col>
                      <Label
                        style={{
                          marginBottom: 10,
                        }}
                        title="Scan Running Instances only"
                      />
                    </Col>
                    <Col>
                      <Form.Item
                        name="scanRunningInstances"
                        initialValue={
                          cloudAccountData.cloudXrayConfig.scanRunningInstances
                            ? cloudAccountData.cloudXrayConfig.scanRunningInstances
                            : false
                        }
                      >
                        <Switch
                          disabled={cloudAccountData.protectionLevel === 'basic'}
                          size="small"
                          checkedChildren={<Icon type="check" />}
                          unCheckedChildren={<Icon type="close" />}
                          defaultChecked={
                            cloudAccountData.cloudXrayConfig.scanRunningInstances
                              ? cloudAccountData.cloudXrayConfig.scanRunningInstances
                              : false
                          }
                        />
                      </Form.Item>
                    </Col>
                  </div>
                  <Col>
                    <p>
                      {cloudAccountData.cloudXrayConfig.scanRunningInstances
                        ? 'Enabling this configuration will make Cloudxray scan only your running  instances - not the stopped instances.'
                        : 'Disabling this configuration will make Cloudxray scan all your instances - running instances as well as stopped instances'}
                    </p>
                  </Col>
                </Row>
              </>
            )}
            <Form.Item style={{ margin: 0, marginTop: 44 }}>
              <Button
                loading={props.updating}
                type="primary"
                htmlType="submit"
                style={{ float: 'left' }}
              >
                Update
              </Button>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Label
              style={{
                marginBottom: 10,
              }}
              title="Protection Level"
            />
            {cardData.map((data, index) => (
              <ModeCards
                isLastIndex={index === cardData.length - 1}
                data={data}
                modeCheckbox={modeCheckbox || 'basic'}
                setModeCheckbox={setModeCheckbox}
                provider={cloudAccountData.provider}
              />
            ))}
          </Col>
        </Row>
      </Form>

      <Divider />

      <DeleteCloudAccountWrapper
        handleDeleteAccount={props.handleDeleteAccount}
        handleDisbaledAccount={props.handleDisbaledAccount}
        confirmCleanup={props.confirmCleanup}
        showDisableAction={cloudAccountData.isActive}
        cloudAccountName={cloudAccountData.accountName}
        cloudAccountData={cloudAccountData}
      />
    </>
  );
};

// export default AdvancedAWSConfiguration;
export default connect(({ cloudAccount, login }) => ({
  updating: cloudAccount.updating,
  allCloudAccountsList: cloudAccount.allCloudAccountsList,
  xrayEnabledCloudProviders: login.xrayEnabledCloudProviders,
}))(AdvancedAWSConfiguration);

const RoleARNTooltip = () => {
  const [visible, setVisible] = useState(false);
  const showModal = () => {
    setVisible(true);
  };
  const handleOk = e => {
    setVisible(false);
  };
  const handleCancel = e => {
    setVisible(false);
  };

  return (
    <span>
      Role ARN&nbsp;
      <Tooltip title="Help">
        <QuestionCircleOutlined style={{ verticalAlign: 0 }} onClick={showModal} />
      </Tooltip>
      <Modal
        title="Amazon Resource Number (ARN)"
        visible={visible}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <p>
          <b>Step 1: </b>
          To create IAM Role, log in to your AWS IAM console{' '}
          <a target="blank" href="https://signin.aws.amazon.com/">
            {' '}
            <b>https://signin.aws.amazon.com/</b>{' '}
          </a>
          .
        </p>
        <p>
          <b>Step 2: </b>
          Click 'Roles' in side menu, click on "Create Role" button.
        </p>
        <p>
          <b>Step 3: </b>
          Click on 'Another AWS Account' box, copy your Account ID from cloudnosys dashboard and
          paste it in the Account field of aws console.
        </p>
        <p>
          <b>Step 4: </b>
          Click on the "Require external ID" checkbox, copy the external Id from your cloudnosys
          dashboard and paste it the External Id field of aws console, then click next.
        </p>
        <p>
          <b>Step 5: </b>
          In the next screen that appears, search for your desired policy and add it, click next.
        </p>
        <p>
          <b>Step 6: </b>
          Enter valid name and description in next Review Policy screen, then click Create Policy.
        </p>
        <p>
          <b>Step 7: </b>
          This would successfully create your IAM Role and generate a <b>Role ARN</b>.
        </p>
        <p>
          <b>Step 8: </b>
          To get your Role ARN, navigate back to <b>Roles</b> in AWS console, locate your Role name,
          click on that and it would open up your role summary, displaying your <b>Role ARN</b> on
          the top.
        </p>
        <p>
          <b>Step 9: </b>
          Copy <b>Role ARN</b> from AWS console and paste it in the Role ARN field in Cloudnosys
          Dashboard.
        </p>
      </Modal>
    </span>
  );
};
